import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { useAppBootstrap } from "@/context/AppBootstrapContext";
import {
  drillDirection,
  type DrillDirection,
} from "@/lib/routeDrillTransition";
import { cn } from "@/lib/utils";

const PAGE_ENTER_MS = 380;

/**
 * Route + boot enter — glass panels fade themselves in (no full-page mask).
 * List → detail drill-ins use directional stagger via `.page-drill-*`.
 */
/** Wraps the layout {@link Outlet} — pass `<Outlet key={…} />` from the shell layout. */
export function PageTransition({ children }: { children: ReactNode }) {
  const location = useLocation();
  const onKaiPage = location.pathname === "/";
  const onHomePage =
    location.pathname === "/dashboard" || location.pathname === "/preview";
  const { phase } = useAppBootstrap();
  const [pageEnter, setPageEnter] = useState(false);
  const [drill, setDrill] = useState<DrillDirection>("none");
  const prevPathRef = useRef(location.pathname);
  const hasBootedRef = useRef(false);

  useEffect(() => {
    if (phase === "exiting") {
      setPageEnter(true);
      setDrill("none");
      return;
    }
    if (phase !== "ready") {
      prevPathRef.current = location.pathname;
      return;
    }

    if (!hasBootedRef.current) {
      hasBootedRef.current = true;
      prevPathRef.current = location.pathname;
      setPageEnter(false);
      setDrill("none");
      return;
    }

    if (prevPathRef.current === location.pathname) return;

    setDrill(drillDirection(prevPathRef.current, location.pathname));
    prevPathRef.current = location.pathname;
    setPageEnter(true);
    document.querySelector("main")?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname, phase]);

  useEffect(() => {
    if (!pageEnter) return;
    const timer = window.setTimeout(() => {
      setPageEnter(false);
      setDrill("none");
    }, PAGE_ENTER_MS);
    return () => window.clearTimeout(timer);
  }, [pageEnter, location.pathname]);

  const drillActive = pageEnter && drill !== "none";

  return (
    <div
      className={cn(
        "boot-page-content relative isolate z-[1] flex min-h-0 w-full min-w-0 flex-1 flex-col",
        onKaiPage && "h-full",
        pageEnter && !onHomePage && drill === "none" && "page-enter",
        drillActive && drill === "forward" && "page-drill-forward",
        drillActive && drill === "back" && "page-drill-back",
      )}
    >
      {children}
    </div>
  );
}
